import { useEffect, useMemo, useState } from "react";
import { createGlobalStyle, styled } from "styled-components";
import {
  Authority,
  ICurrentUser,
  IProduct,
  IProductType,
  getAllProducts,
  scrollBarStyles,
} from "@dataone/lib";
import { Image, Progress, Space, Button, Tooltip, Flex } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";

import { Link, useNavigate } from "react-router-dom";
import ChartProfile from "./ChartProfile";
import { useQuery } from "@tanstack/react-query";
import Modal from "./Modal";
import {
  CheckMarkSimpleIcon,
  InfoIcon,
  MessageIcon,
  PointingArrowIcon,
} from "@dataone/icons";
import BackgroundRenderer from "../../package-management/components/package-card/BackgroundRenderer";
import dayjs from "dayjs";

interface ProfileSubscriptionProps {
  user: ICurrentUser;
}

const ProfileSubscription = ({ user }: ProfileSubscriptionProps) => {
  const navigate = useNavigate();
  const subscription = user?.subscription!;

  const conicColors = {
    "50%": "#8D4CFE",
    "100%": "#4974F6",
  };

  let chat: any = (subscription.chatUsage * 100) / subscription.chatLimit;
  let search: any = (subscription.searchUsage * 100) / subscription.searchLimit;
  let promptUpdate: any =
    (subscription.promptGridItemUpdateUsage * 100) /
    subscription.promptGridItemUpdateLimit;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSearchIncrease, setIsSearchIncrease] = useState<boolean>(false);
  const [isPromptChartUpdate, setIsPromptChartUpdate] =
    useState<boolean>(false);
  const [productsData, setProductsData] = useState<IProduct[]>([]);
  const [showPayment, setShowPayment] = useState<boolean>(false);
  const openSearchIncreaseModal = () => {
    setIsSearchIncrease(true);
    setIsOpen(true);
  };
  const openPrompChartUpdateModal = () => {
    setIsPromptChartUpdate(true);
    setIsOpen(true);
  };
  const openChatIncreaseModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setIsSearchIncrease(false);
    setIsPromptChartUpdate(false);
    setShowPayment(false);
  };

  const { data: products } = useQuery({
    queryKey: ["all-products"],
    queryFn: getAllProducts,
  });

  useEffect(() => {
    setProductsData(products!);
  }, [products]);

  return (
    <StyledProfileSubscription>
      <PageTitle>Usage</PageTitle>
      <StyledCardsUsage>
        <StyledCard>
          <div>
            <StyledTitle> Available AI search</StyledTitle>

            <div style={StyledSearchUsage}>
              <p style={StyledSup}>{subscription.searchUsage}</p>
              <p style={StyledSub}>/{subscription.searchLimit}</p>
            </div>

            {subscription?.type !== "FREE" &&
              productsData?.find((p) => p.type === IProductType.SEARCH)
                ?.minimumQuantity! <=
                subscription.searchLimit - subscription.searchUsage && (
                <Link to={""} style={StyledLink}>
                  <span className="pointing-icon">
                    <PointingArrowIcon />
                  </span>
                  <label
                    onClick={openSearchIncreaseModal}
                    style={StyledLabel}
                    htmlFor="enable"
                  >
                    Increase Usage
                  </label>
                </Link>
              )}
          </div>
          <StyledProgress>
            <Space style={{ marginTop: "10px" }}>
              <Progress
                strokeColor={conicColors}
                type="dashboard"
                size={80}
                percent={search.toFixed(0)}
                format={(chat: number | undefined) => `${chat} %`}
              />
            </Space>
            {/* <QuestionMarkIcon /> */}
          </StyledProgress>
          <GlobalStyle />
          <Tooltip
            placement="bottom"
            title="Monitor your AI-powered search usage with real-time updates and the option to increase your limit as needed."
            trigger={["hover"]}
            className="info-icon"
          >
            <span>
              <InfoIcon />
            </span>
          </Tooltip>
        </StyledCard>
        <StyledCard>
          <div
          // style={{
          //   width: "65%",
          // }}
          >
            <StyledTitle> Available AI consultancy </StyledTitle>

            <div style={StyledChatUsage}>
              <p style={StyledSup}>{subscription.chatUsage}</p>
              <p style={StyledSub}>/{subscription.chatLimit}</p>
            </div>
            {subscription?.type !== "FREE" &&
              productsData?.find((p) => p.type === IProductType.CHAT)
                ?.minimumQuantity! <=
                subscription.chatLimit - subscription.chatUsage && (
                <Link to={""} style={StyledLink}>
                  <span className="pointing-icon">
                    <PointingArrowIcon />
                  </span>
                  <label
                    onClick={openChatIncreaseModal}
                    style={StyledLabel}
                    htmlFor="enable"
                  >
                    Increase Usage
                  </label>
                </Link>
              )}
          </div>
          <StyledProgress>
            <Space style={{ marginTop: "10px" }}>
              <Progress
                strokeColor={"#16CCBF"}
                type="dashboard"
                size={80}
                percent={chat.toFixed(0)}
                format={(chat: number | undefined) => `${chat} %`}
              />
            </Space>
            {/* <QuestionMarkIcon /> */}
          </StyledProgress>
          <Tooltip
            placement="bottom"
            title="Monitor your AI consultancy usage with real-time updates and the option to increase your limit as needed."
            trigger={["hover"]}
            className="info-icon"
          >
            <span>
              <InfoIcon />
            </span>
          </Tooltip>
        </StyledCard>
        <StyledCard>
          <div
          // style={{
          //   width: "65%",
          // }}
          >
            <StyledTitle> Update chart with prompt </StyledTitle>

            <div style={StyledPromptUpdateUsage}>
              <p style={StyledSup}>{subscription.promptGridItemUpdateUsage}</p>
              <p style={StyledSub}>/{subscription.promptGridItemUpdateLimit}</p>
            </div>
            {subscription?.type !== "FREE" &&
              productsData?.find(
                (p) => p.type === IProductType.PROMPT_CHART_UPDATE
              )?.minimumQuantity! <=
                subscription.promptGridItemUpdateLimit -
                  subscription.promptGridItemUpdateUsage && (
                <Link to={""} style={StyledLink}>
                  <span className="pointing-icon">
                    <PointingArrowIcon />
                  </span>
                  <label
                    onClick={openPrompChartUpdateModal}
                    style={StyledLabel}
                    htmlFor="enable"
                  >
                    Increase Usage
                  </label>
                </Link>
              )}
          </div>
          <StyledProgress>
            <Space style={{ marginTop: "10px" }}>
              <Progress
                strokeColor={"#FFB800"}
                type="dashboard"
                size={80}
                percent={promptUpdate.toFixed(0)}
                format={(promptUpdate: number | undefined) =>
                  `${promptUpdate} %`
                }
              />
            </Space>
            {/* <QuestionMarkIcon /> */}
          </StyledProgress>
          <Tooltip
            placement="bottom"
            title="Easily modify and update charts using prompts, Monitor your usage with real-time updates and the option to increase your limit as needed."
            trigger={["hover"]}
            className="info-icon"
          >
            <span>
              <InfoIcon />
            </span>
          </Tooltip>
        </StyledCard>
        <Modal
          isOpen={isOpen}
          close={handleCloseModal}
          subscription={subscription}
          productsData={productsData}
          isSearchIncrease={isSearchIncrease}
          isPromptChartUpdate={isPromptChartUpdate}
          showPayment={showPayment}
          setShowPayment={setShowPayment}
        />
      </StyledCardsUsage>
      <StyledStatisticUserData>
        <StyledUsageChart>
          <StyledChartTitle>Usage chart</StyledChartTitle>
          <StyledChart>
            <ChartProfile widthChart="35vw" heightChart="270px" />
          </StyledChart>
        </StyledUsageChart>
        <StyledBorder></StyledBorder>

        <StyledUsagePremium>
          <StyledPremiumTitle>
            <StyledParagraph> {subscription.title} </StyledParagraph>
            <StyledParagraph>
              {/* $ {subscription.price}
              <span style={{ fontSize: "13px" }}>
                / {subscription.type.toLowerCase()}
              </span> */}
              {"Expires on " +
                dayjs(subscription.currentPeriodEnd).format(
                  "YYYY-MM-DD [at] HH:mm"
                )}
            </StyledParagraph>
          </StyledPremiumTitle>
          <StyledPremiumPackage>
            <StylePremiumPackageContent>
              {subscription.features?.map((feature) => (
                <StyledList
                  style={{
                    listStyleType: "none",
                    padding: "0",
                  }}
                >
                  <li>
                    <CheckMarkSimpleIcon />
                    {feature}
                  </li>
                </StyledList>
              ))}
            </StylePremiumPackageContent>
          </StyledPremiumPackage>
        </StyledUsagePremium>
      </StyledStatisticUserData>
      <StyledFooterUserData>
        <StyledFirstSection>
          <StyledFooterContent>
            <div className="msg-icon">
              <MessageIcon />
            </div>
            <div>
              <p style={{ fontWeight: "bold", fontSize: "14px" }}>
                Do you have any question ?
              </p>
              <p style={{ fontSize: "12px" }}>
                check support center to find the answer
              </p>
            </div>
          </StyledFooterContent>
          <div>
            <StyledButtonContactUs onClick={() => navigate("/support")}>
              Help center
            </StyledButtonContactUs>
          </div>
        </StyledFirstSection>
        {user?.groups[0] &&
          (user?.groups[0] === Authority.OG_SUBSCRIBER ||
            user?.groups[0] === Authority.OG_SUBSCRIBER_EX ||
            user?.groups[0] === Authority.OG_GUEST) && (
            <StyledUpgrade>
              <div>
                {/* <StyleImage>
                  <Image
                    preview={false}
                    width={100}
                    src="/images/profile/upgrade.png"
                    alt="Upgrade Image"
                  />
                </StyleImage> */}
                <p style={StyleParagraph}>
                  {user.groups[0] === Authority.OG_GUEST ||
                  user?.groups[0] === Authority.OG_SUBSCRIBER_EX
                    ? "Choose a subscription plan that suits your needs"
                    : user?.subscription?.type === "FREE"
                    ? "Upgrade for the complete experience"
                    : "Explore other plans to get more features"}
                </p>
                <StyleButton onClick={() => navigate("/subscription")}>
                  {user.groups[0] === Authority.OG_GUEST ||
                  user?.groups[0] === Authority.OG_SUBSCRIBER_EX
                    ? "Subscribe now"
                    : user?.subscription?.type === "FREE"
                    ? "Upgrade now"
                    : "Explore plans"}
                  <ArrowRightOutlined />
                </StyleButton>
              </div>
            </StyledUpgrade>
          )}
      </StyledFooterUserData>
    </StyledProfileSubscription>
  );
};

export default ProfileSubscription;

const StyledFooterContent = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;
  @media only screen and (max-width: 1118px) {
    gap: 1rem;
  }
  .msg-icon {
    font-size: 50px;
    display: flex;
  }
`;

const StyledButtonContactUs = styled.button`
  min-height: 34px;
  width: 120px;
  border-radius: 6px;
  background: #4974f6;
  color: #eff1fd;
  font-size: 14px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  @media only screen and (max-width: 1118px) {
    font-size: 12px;
  }
`;

const StyledList = styled.ul`
  color: #040726;
  font-size: 12px;
  font-weight: 700;
  li {
    display: flex;
    align-items: center;
  }

  svg {
    font-size: 25px;
  }
`;

const StylePremiumPackageContent = styled.div`
  font-weight: bold;
  height: 270px;
  margin-top: 30px;
  overflow: auto;
  ${scrollBarStyles};
`;

const StyledBorder = styled.div`
  border-radius: 30px;
  background-color: #fff;
  width: 3px;
  margin-top: 16.6px;
`;

const StyledStatisticUserData = styled.div`
  margin-bottom: 35px;
  background-color: #eff1fd;
  border-radius: 15px;
  display: flex;
  gap: 15px;
  padding-bottom: 30px;
  flex-wrap: nowrap;
`;

const StyledUsageChart = styled.div`
  flex: 1 1 45%;
  padding-left: 30px;
`;

const StyledChart = styled.h2`
  text-align: center;
  margin: 0;
  min-width: 350px;
`;

const StyledChartTitle = styled.h2`
  color: #4974f6;
  font-size: 19px;
  font-weight: 700;
`;

const StyledPremiumPackage = styled.div``;

const StyledPremiumTitle = styled.h2`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #040726;
  font-size: 20px;
  font-weight: 700;
  margin-top: 5px;
  background-image: url("images/usage/package-background.svg");
  background-repeat: no-repeat;
  padding: 0px 10px 19px 10px;
`;

const StyledParagraph = styled.div`
  margin-top: 16.6px;
  font-weight: bold;
  color: #4974f6;
  font-size: 19px;
  font-weight: 700;
  z-index: 1;
`;

const StyledUsagePremium = styled.div`
  flex: 1 1 45%;
  padding: 0 30px;
  flex-basis: inherit;
  width: 385px;
`;

const StyledProfileSubscription = styled.div`
  padding: 0 30px 0 30px;
  width: 100%;
  @media only screen and (max-width: 1118px) {
    padding: 0 10px;
    width: 100%;
  }
`;

const StyledCardsUsage = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: nowrap;
  // gap: 14px;
  @media only screen and (max-width: 1118px) {
    gap: 0;
    justify-content: space-between;
  }
`;
const GlobalStyle = createGlobalStyle`
.ant-tooltip .ant-tooltip-content{
  margin-right: 32px !important;
}
`;
const StyledCard = styled.div`
  max-height: 120px;
  min-width: 200px;
  width: 33%;
  height: 170px;
  background-color: #eff1fd;
  padding: 15px 13px;
  border-radius: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  position: relative;
  @media only screen and (max-width: 1118px) {
    padding: 10px 5px;
    justify-content: space-between;
  }
  .pointing-icon {
    color: #1c1b1f;
    display: block;
    height: 14px;
    width: 14px;
  }
  .info-icon {
    position: absolute;
    right: 10px;
    top: 10px;
    &:hover {
      cursor: pointer;
    }
  }
`;

const StyledProgress = styled.div`
  width: 52%;
  display: flex;
  justify-content: end;
  .ant-progress-text {
    color: #000 !important;
    font-weight: bold !important;
  }
`;

const StyledFirstSection = styled.div`
  min-width: 450px;
  flex: 1 1 40%;
  border-radius: 10px;
  background: #eff1fd;
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 1em;
  padding: 3%;
  max-height: 140px;
  @media only screen and (max-width: 1118px) {
    min-width: 430px;
  }
`;

const StyledUpgrade = styled.div`
  display: flex;
  align-items: center;
  min-width: 390px !important;
  position: relative;
  flex: 1 1 20%;
  border-radius: 10px;
  background: #eff1fd;
  padding-left: 15px;
  padding-right: 15px;
  // text-align: center;
  max-width: 270px;
  max-height: 140px;
  @media only screen and (max-width: 1118px) {
    min-width: 150px;
  }
  background-image: url("images/usage/upgrade-background.svg");
  background-size: cover;
  background-repeat: no-repeat;
`;

const StyledFooterUserData = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 30px;
  margin-bottom: 30px;
  padding-bottom: 10px;
  @media only screen and (max-width: 1118px) {
    gap: 10px;
  }
`;

const StyleButton = styled(Button)`
  border-radius: 6px;
  background: transparent;
  padding-left: 0 !important;
  color: #eff1fd !important;
  font-size: 16px;
  font-weight: bold;
  border: none;
  * @media only screen and (max-width: 1118px) {
    font-size: 12px;
  }
`;

const StyleParagraph = {
  color: "#ffffff",
  fontSize: "15px",
  fontWeight: "bold",
  marginTop: "20px",
  marginBottom: "17px",
  width: "165px",
};

const StyleImage = styled.div`
  margin-top: -25px;
`;

const StyledTitle = styled.p`
  margin: 0;
  font-size: 14px !important;
  font-weight: bold;
  width: max-content;
  @media only screen and (max-width: 1118px) {
    margin: 0;
    font-size: 12px !important;
    font-weight: bold;
    width: 90px;
  }
`;

const StyledSearchUsage = {
  color: "#4A73F6",
  marginBottom: "10px",
  display: "flex",
  justifyContent: "left",
  alignItems: "center",
  margin: 0,
  fontWeight: "bold",
};

const StyledChatUsage = {
  color: "#16CCBF",
  marginBottom: "10px",
  display: "flex",
  justifyContent: "left",
  alignItems: "center",
  margin: 0,
  fontWeight: "bold",
};

const StyledPromptUpdateUsage = {
  color: "#FFB800",
  marginBottom: "10px",
  display: "flex",
  justifyContent: "left",
  alignItems: "center",
  margin: 0,
  fontWeight: "bold",
};

const StyledSup = {
  fontWeight: "bold",
  fontSize: "14px",
};

const StyledSub = {
  fontSize: "10px",
};

const StyledLink = {
  display: "flex",
  alignItems: "center",
};

const StyledLabel = {
  color: "#040726",
  marginLeft: "5px",
  cursor: "pointer",
};
const PageTitle = styled.div`
  color: #4974f6;
  font-size: 19px;
  font-weight: 700;
  padding: 15px 0;
`;
